/**
 * Модель виджета «Предписания карантина» (agroai-0e8): цвет случая и шкала
 * частоты. Чистые функции — проверяются `prescriptionsModel.test.ts`.
 *
 * Цвет случая (полоса строки и карта состояния) — ЦВЕТ СЛУЧАЯ КАРАНТИНА,
 * т. е. статус единицы земли (контур/часть) на дату среза (решение владельца):
 * - `red`    — красный случай (A1/A2, повышенный риск);
 * - `yellow` — жёлтый, под наблюдением (открыт, статус не красный);
 * - `green`  — устранено замером инспектора.
 * Просрочка предписания — ОТДЕЛЬНЫЙ признак (метка «после срока», пунктирная
 * обводка на карте), цвет она не меняет.
 *
 * Шкала частоты (тепловая карта): сколько РАЗНЫХ предписаний получил контур
 * за период области (цикл / хозяйство / район). Пороги — квантили по
 * распределению той же области: первая ступень — одиночный случай (минимум),
 * остальные четыре — квартили контуров с повторами. Совпавшие квартили
 * (у района сотни контуров с «2») сдвигаются на следующее встреченное
 * значение — иначе 3 и 9 легли бы в одну ступень. Рецидив (проблема вернулась
 * ПОСЛЕ устранения замером) — отдельный признак обводкой, не ступень шкалы.
 */
import type { HeatTone } from '../../map/layers';

export type CaseTone = 'red' | 'yellow' | 'green';

export function caseTone(work: { eliminated_on: string | null; unit_status?: string | null }): CaseTone {
  if (work.eliminated_on || work.unit_status === 'green') return 'green';
  return work.unit_status === 'red' ? 'red' : 'yellow';
}

/** Этапы случая по порядку (agroai-0e8): находка → предписание → работы
 * хозяйства/отчёт → замер инспектора → решение или устранение. */
export const STAGES = ['finding', 'requirement', 'report', 'followup', 'resolved'] as const;
export type Stage = (typeof STAGES)[number];
/** Шаг таймлайна: пройден, текущий (ждём его), просрочен, пропущен (случай
 * ушёл дальше без него — замер без отчёта хозяйства), ещё впереди. */
export type StepState = 'done' | 'current' | 'late' | 'skipped' | 'todo';

export interface StageInput {
  eliminated_on: string | null;
  reported_on?: string | null;
  followup_on?: string | null;
  followup_outcome?: string | null;
  decision_kind?: string | null;
  overdue?: boolean;
}

/** Где случай сейчас: последний пройденный этап. */
export function caseStage(w: StageInput): Stage {
  if (w.eliminated_on) return 'resolved';
  if (w.followup_on) return 'followup';
  if (w.reported_on) return 'report';
  return 'requirement';
}

/**
 * Состояние каждого шага таймлайна. Находка и предписание у строки есть
 * всегда (работа существует только по предписанию). Решение ответственного
 * без устранения — шаг «решение» пройден, но случай открыт: замер ждём снова.
 * Просрочка красит только ожидаемый шаг («отчёт» или «замер»), не случай.
 * Шаг, которого не было (замер без отчёта, устранение актом без замера), —
 * «пропущен», а не «пройден»: таймлайн не выдумывает отчёт.
 */
export function stepStates(w: StageInput): Record<Stage, StepState> {
  const resolved = Boolean(w.eliminated_on);
  const followed = Boolean(w.followup_on);
  const reported = Boolean(w.reported_on);
  const waiting: StepState = w.overdue ? 'late' : 'current';
  return {
    finding: 'done',
    requirement: 'done',
    report: reported ? 'done' : followed || resolved ? 'skipped' : waiting,
    followup: followed ? 'done' : resolved ? 'skipped' : reported ? waiting : 'todo',
    resolved: resolved || w.decision_kind ? 'done' : followed ? waiting : 'todo',
  };
}

export interface ContourCounts {
  requirements: number;
  open_requirements: number;
  red_requirements?: number;
}

/** Цвет контура на карте состояния при фильтре; `null` — контур фильтром не взят. */
export function contourTone(c: ContourCounts, filter: 'open' | 'closed' | 'all'): CaseTone | null {
  const open = c.open_requirements > 0;
  const closed = c.requirements > c.open_requirements;
  if (filter === 'closed') return closed ? 'green' : null;
  if (filter === 'open' && !open) return null;
  if (!open) return 'green';
  return (c.red_requirements ?? 0) > 0 ? 'red' : 'yellow';
}

export interface HeatBin {
  /** Нижняя граница ступени (включительно). */
  from: number;
  /** Верхняя граница (включительно): следующий порог минус один, у последней — максимум. */
  to: number;
  tone: HeatTone;
}

const TONES: readonly HeatTone[] = ['heat-1', 'heat-2', 'heat-3', 'heat-4', 'heat-5'];

/** Квантиль по ближайшему рангу (значение, встреченное в данных). */
function nearestRank(sorted: number[], p: number): number {
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[index]!;
}

/** Ступени шкалы частоты по распределению области (до пяти). */
export function heatBins(counts: number[]): HeatBin[] {
  const values = counts.filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  if (values.length === 0) return [];
  const min = values[0]!;
  const upper = values.filter((n) => n > min);
  const distinct = [...new Set(upper)];
  const bounds = [min];
  if (upper.length > 0) {
    for (const candidate of [upper[0]!, nearestRank(upper, 0.25), nearestRank(upper, 0.5), nearestRank(upper, 0.75)]) {
      const last = bounds[bounds.length - 1]!;
      const next = candidate > last ? candidate : distinct.find((v) => v > last);
      if (next !== undefined) bounds.push(next);
    }
  }
  const max = values[values.length - 1]!;
  const k = bounds.length;
  return bounds.map((from, i) => {
    // Пороги сплошные (целые числа): ступень до следующего порога минус один.
    const nextFrom = bounds[i + 1];
    const to = nextFrom === undefined ? max : nextFrom - 1;
    const toneIndex = k === 1 ? 2 : k === TONES.length ? i : Math.round((i * (TONES.length - 1)) / (k - 1));
    return { from, to, tone: TONES[toneIndex]! };
  });
}

export function heatBinOf(n: number, bins: HeatBin[]): HeatBin | null {
  let found: HeatBin | null = null;
  for (const bin of bins) if (n >= bin.from) found = bin;
  return n > 0 ? found : null;
}

/** Подпись ступени: «1», «3–4», «10–15». */
export function binLabel(bin: HeatBin): string {
  return bin.from === bin.to ? String(bin.from) : `${bin.from}–${bin.to}`;
}
